import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useOrderDetails,
  UpdateOrderStatus,
  useAssignWaiter,
} from "../hooks/useOrders";
import { usePayment, useUpdatePaymentStatus } from "../hooks/usePayments";
import { message, Spin, Alert, Select, Button } from "antd";
import OrderDetailsTable from "../components/orders/details/OrderDetailsTable";
import MessageSection from "../components/orders/details/MessageSection";
import ActivityLog from "../components/orders/details/ActivityLog";
import CustomerInfo from "../components/orders/details/CustomerInfo";
import PaymentDetails from "../components/orders/details/PaymentDetails";
import OrderCompletionModal from "../components/orders/details/OrderCompletionModal";
import PaymentConfirmationModal from "../components/orders/details/PaymentConfirmationModal";
import { Icon } from "@iconify/react";
import { usePermissions } from "../context/PermissionContext";
import { PERMISSIONS } from "../utils/permissions";
import { useAuth } from "../hooks/useAuth";
import { useStaff } from "../hooks/useSchedule";

const OrderDetailsPage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const { updatePaymentStatus } = useUpdatePaymentStatus();
  const { Option } = Select;
  const {
    data: order,
    isLoading: orderLoading,
    refetch,
  } = useOrderDetails(Number(orderId));
  const { data: payment, isLoading: paymentLoading } = usePayment(
    Number(orderId)
  );
  const { data: user } = useAuth();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);

  // Add local state to track payment status
  const [localPaymentStatus, setLocalPaymentStatus] = useState<
    number | undefined
  >(undefined);

  // Waiter assignment state
  const [isEditingWaiter, setIsEditingWaiter] = useState(false);
  const [selectedWaiter, setSelectedWaiter] = useState<string | null>(null);

  // Waiter hooks
  const { data: waiters, isLoading: loadingWaiters } = useStaff();
  const { mutate: assignWaiter, isPending: assigningWaiter } =
    useAssignWaiter();

  // Get permissions
  const { hasPermission } = usePermissions();
  const canViewOrders = hasPermission(PERMISSIONS.ACCESS_ORDERS);
  const canViewOrderDetails = hasPermission(PERMISSIONS.VIEW_ORDERS_DETAILS);
  const canUpdateOrderStatus = hasPermission(PERMISSIONS.UPDATE_ORDER_STATUS);
  const canProcessPayments = hasPermission(PERMISSIONS.PROCESS_PAYMENTS);
  const canViewAllUsersOrders = hasPermission(
    PERMISSIONS.VIEW_ALL_USERS_ORDERS
  );

  // Permission to assign waiters - only for users who can view all orders or admins
  const canAssignWaiter = hasPermission(PERMISSIONS.MODIFY_ORDERS);

  // Check if user can access this specific order
  const canAccessThisOrder = () => {
    if (canViewAllUsersOrders) return true;
    if (user?.name !== order?.waiter) return false;
  };

  // Check if waiter assignment can be edited (only unassigned orders)
  const canEditWaiterAssignment =
    canAssignWaiter && (!order?.waiter || order.waiter.trim() === "");

  // Handle waiter assignment
  const handleAssignWaiter = () => {
    if (!selectedWaiter) {
      message.warning("Please select a waiter");
      return;
    }

    assignWaiter(
      { orderId: Number(orderId), waiterName: selectedWaiter },
      {
        onSuccess: () => {
          setIsEditingWaiter(false);
          setSelectedWaiter(null);
          refetch();
        },
      }
    );
  };

  // Handle unassigning waiter
  const handleUnassignWaiter = () => {
    assignWaiter(
      { orderId: Number(orderId), waiterName: null },
      {
        onSuccess: () => {
          setIsEditingWaiter(false);
          setSelectedWaiter(null);
          refetch();
        },
      }
    );
  };

  // Handle starting waiter edit mode
  const handleStartEditWaiter = () => {
    setIsEditingWaiter(true);
    setSelectedWaiter(order?.waiter || null);
  };

  // Handle canceling waiter edit
  const handleCancelEditWaiter = () => {
    setIsEditingWaiter(false);
    setSelectedWaiter(null);
  };

  // Initialize localPaymentStatus from order data when it loads
  React.useEffect(() => {
    if (order) {
      setLocalPaymentStatus(order.payment_status_id);
    }
  }, [order]);

  // Redirect if user doesn't have permission to view order details at all
  React.useEffect(() => {
    if (!canViewOrderDetails) {
      const timeout = setTimeout(() => {
        navigate("/unauthorized");
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [canViewOrderDetails, navigate]);

  // Check payment status - use localPaymentStatus when available
  const effectivePaymentStatus =
    localPaymentStatus !== undefined
      ? localPaymentStatus
      : payment?.status || order?.payment_status_id;

  const isPaymentPending = effectivePaymentStatus === 1;
  const isPaymentConfirmed = effectivePaymentStatus === 2;

  // Generate the activity log dynamically based on the order status
  const generateActivityLog = (
    orderStatusId: number,
    paymentConfirmed: boolean
  ): any[] => {
    const formatTime = (date: string) => {
      return new Date(date).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    };

    const currentTime = formatTime(new Date().toISOString());
    const orderTime = order?.created_at
      ? formatTime(order.created_at)
      : currentTime;

    const activities: any[] = [
      {
        time: orderTime,
        title: "Order Placed",
        description: [
          { time: orderTime, text: "Customer successfully placed order" },
        ],
        color: "#4299E1", // Blue
        icon: <Icon icon="mdi:cart-check" className="text-lg" />,
      },
    ];

    if (orderStatusId >= 2) {
      activities.push({
        time: currentTime,
        title: "Order Confirmed",
        description: [
          {
            time: currentTime,
            text: "Order was confirmed to be available",
          },
        ],
        color: "#48BB78", // Green
        icon: <Icon icon="mdi:clipboard-check" className="text-lg" />,
      });
    }

    if (orderStatusId >= 3) {
      activities.push({
        time: currentTime,
        title: "Preparation",
        description: [
          {
            time: currentTime,
            text: "Order forwarded to process station",
          },
          {
            time: currentTime,
            text: "Order is being prepared",
          },
        ],
        color: "#ECC94B", // Yellow
        icon: <Icon icon="mdi:chef-hat" className="text-lg" />,
      });
    }

    // Status for "Ready"
    if (orderStatusId >= 4) {
      activities.push({
        time: currentTime,
        title: "Order Ready",
        description: [
          {
            time: currentTime,
            text: "Order is ready for serving",
          },
        ],
        color: "#ED8936", // Orange
        icon: <Icon icon="mdi:food-fork-drink" className="text-lg" />,
      });
    }

    // Status for "Served" - ID 5
    if (orderStatusId >= 5) {
      activities.push({
        time: currentTime,
        title: "Order Served",
        description: [
          {
            time: currentTime,
            text: "Order has been served to the customer",
          },
        ],
        color: "#38A169", // Green
        icon: <Icon icon="mdi:food-served" className="text-lg" />,
      });
    }

    // Status for "Completed" - ID 6 (final step)
    if (orderStatusId >= 6) {
      activities.push({
        time: currentTime,
        title: "Order Completed",
        description: [
          {
            time: currentTime,
            text: paymentConfirmed
              ? "Payment confirmed and order marked as completed"
              : "Order marked as completed",
          },
        ],
        color: "#805AD5", // Purple
        icon: <Icon icon="mdi:check-circle" className="text-lg" />,
      });
    }

    return activities;
  };

  const activityLog = generateActivityLog(
    order?.order_status.id || 1,
    isPaymentConfirmed
  );

  // Generate order details data dynamically based on the order status
  const getOrderDetailsData = (orderStatusId: number) => {
    switch (orderStatusId) {
      case 1: // Placed
        return {
          statusColor: "bg-blue-100 text-blue-500",
          message: "Order has been placed and is awaiting confirmation.",
        };
      case 2: // Confirm
        return {
          statusColor: "bg-green-100 text-green-600",
          message: "Order has been confirmed and is waiting to be prepared.",
        };
      case 3: // Preparing
        return {
          statusColor: "bg-yellow-100 text-yellow-600",
          message: "Order is currently being prepared in the kitchen.",
        };
      case 4: // Ready
        return {
          statusColor: "bg-orange-100 text-orange-600",
          message: "Order is ready and waiting to be served.",
        };
      case 5: // Served
        return {
          statusColor: "bg-green-100 text-green-600",
          message: "Order has been served to the customer.",
        };
      case 6: // Completed
        return {
          statusColor: "bg-purple-500 text-white",
          message: "Order has been completed.",
        };
      default:
        return {
          statusColor: "bg-gray-100 text-gray-500",
          message: "Order status unknown.",
        };
    }
  };

  const orderDetailsData = getOrderDetailsData(order?.order_status.id || 1);

  const handleConfirmPayment = async () => {
    if (!canProcessPayments) {
      message.error("You don't have permission to process payments");
      return;
    }
    setIsPaymentModalOpen(true);
  };

  const handleCompletePayment = async () => {
    if (!canProcessPayments) {
      message.error("You don't have permission to process payments");
      return;
    }

    try {
      await updatePaymentStatus(Number(orderId), 2); // Update payment status to 2 (confirmed)

      // Update local state immediately
      setLocalPaymentStatus(2);

      message.success("Payment confirmed successfully");
      setIsPaymentModalOpen(false);

      // Still refetch in the background to ensure data consistency
      refetch();
    } catch (error) {
      console.error("Failed to complete payment:", error);
      message.error("Failed to confirm payment");
    }
  };

  const handleConfirmOrder = async () => {
    if (!canUpdateOrderStatus) {
      message.error("You don't have permission to update order status");
      return;
    }

    try {
      await UpdateOrderStatus(Number(orderId), 2); // Update status to "Confirm"
      await refetch();
      message.success("Order confirmed successfully");
    } catch (error) {
      console.error("Failed to update order status:", error);
      message.error("Failed to confirm order");
    }
  };

  const handleMoveToPreparing = async () => {
    if (!canUpdateOrderStatus) {
      message.error("You don't have permission to update order status");
      return;
    }

    try {
      await UpdateOrderStatus(Number(orderId), 3); // Update status to "Preparing"
      await refetch();
      message.success("Order moved to preparation");
    } catch (error) {
      console.error("Failed to update order status:", error);
      message.error("Failed to move order to preparation");
    }
  };

  const handleMoveToReady = async () => {
    if (!canUpdateOrderStatus) {
      message.error("You don't have permission to update order status");
      return;
    }

    try {
      await UpdateOrderStatus(Number(orderId), 4); // Update status to "Ready"
      await refetch();
      message.success("Order marked as ready");
    } catch (error) {
      console.error("Failed to update order status:", error);
      message.error("Failed to mark order as ready");
    }
  };

  const handleServeOrder = async () => {
    if (!canUpdateOrderStatus) {
      message.error("You don't have permission to update order status");
      return;
    }

    try {
      await UpdateOrderStatus(Number(orderId), 5); // Update status to "Served" (ID 5)
      await refetch();
      message.success("Order marked as served");
    } catch (error) {
      console.error("Failed to update order status:", error);
      message.error("Failed to mark order as served");
    }
  };

  const handleCompleteOrder = async () => {
    if (!canUpdateOrderStatus) {
      message.error("You don't have permission to update order status");
      return;
    }

    try {
      await UpdateOrderStatus(Number(orderId), 6); // Update status to "Completed" (ID 6)
      setIsModalOpen(false);
      await refetch();
      message.success("Order marked as completed");
    } catch (error) {
      console.error("Failed to update order status:", error);
      message.error("Failed to complete order");
    }
  };

  const calculateTotal = () => {
    if (!order || !order.order_items) return "₦0.00";
    const total = order.order_items.reduce(
      (sum, item) => sum + parseFloat(item.amount) * item.qty,
      0
    );
    return `₦${total.toLocaleString()}`;
  };

  if (orderLoading || paymentLoading) {
    return (
      <div className="flex justify-center py-10">
        <Spin size="large" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex justify-center py-10">
        <p className="text-gray-500">Order not found</p>
      </div>
    );
  }

  // Check if user can view orders at all
  if (!canViewOrders) {
    return (
      <div className="p-6">
        <Alert
          message="Access Denied"
          description="You don't have permission to access orders. Please contact your administrator if you need access."
          type="error"
          showIcon
        />
      </div>
    );
  }

  // Check if user can access this specific order
  if (canAccessThisOrder() === false) {
    return (
      <div className="p-6">
        <Alert
          message="Access Denied"
          description="You don't have permission to view this order. You can only view orders assigned to you or unassigned orders."
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <div className="p-4 bg-white min-h-screen">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 space-y-2 sm:space-y-0">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-500 hover:text-black text-6xl"
          >
            ←
          </button>
          <div className="text-left">
            <h1 className="text-lg sm:text-3xl font-bold">
              ORDER #{order.reference}
            </h1>
            <p className="text-sm text-gray-500">
              {new Date(order.created_at).toLocaleString()}
            </p>
          </div>
        </div>
        <div className="text-left flex items-center space-x-2">
          <h6>Order Status</h6>
          <span
            className={`px-3 py-1 rounded-full text-sm ${orderDetailsData.statusColor}`}
          >
            {order.order_status.name}
          </span>
        </div>
      </div>

      {/* Display who the order is assigned to with edit functionality */}
      <div className="mb-4 p-3 bg-gray-50 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Icon icon="mdi:account-outline" className="mr-2 text-gray-500" />
            <span className="font-medium">Assigned to: </span>

            {!isEditingWaiter ? (
              <div className="flex items-center ml-2">
                <span
                  className={
                    order.waiter ? "text-gray-900" : "text-gray-500 italic"
                  }
                >
                  {order.waiter ? order.waiter : "Unassigned"}
                </span>
                {canEditWaiterAssignment && (
                  <Button
                    type="link"
                    size="small"
                    icon={<Icon icon="mdi:pencil" />}
                    onClick={handleStartEditWaiter}
                    className="ml-2 p-1 h-auto"
                  />
                )}
              </div>
            ) : (
              <div className="flex items-center ml-2 space-x-2">
                <Select
                  placeholder="Select staff member"
                  value={selectedWaiter}
                  onChange={(value) => setSelectedWaiter(value)}
                  loading={loadingWaiters}
                  showSearch
                  optionFilterProp="children"
                >
                  {waiters?.data?.map((staff: any) => (
                    <Option key={staff.id} value={staff.name}>
                      {staff.name} ({staff.location?.name})
                    </Option>
                  ))}
                </Select>
                <Button
                  type="primary"
                  size="small"
                  loading={assigningWaiter}
                  onClick={
                    selectedWaiter === null
                      ? handleUnassignWaiter
                      : handleAssignWaiter
                  }
                  icon={<Icon icon="mdi:check" />}
                />
                <Button
                  size="small"
                  onClick={handleCancelEditWaiter}
                  icon={<Icon icon="mdi:close" />}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <OrderDetailsTable orderItems={order.order_items} />
          <MessageSection message={order?.additional_information ?? ""} />
          <ActivityLog activities={activityLog} />

          {/* Only show action buttons if user has permission to update order status */}
          {canUpdateOrderStatus && (
            <div className="text-center sm:text-left">
              {order.order_status.id === 1 && (
                <button
                  className="bg-primary text-white px-6 py-2 rounded-lg flex items-center justify-center sm:justify-start"
                  onClick={handleConfirmOrder}
                >
                  Confirm Order
                  <span className="ml-2">→</span>
                </button>
              )}

              {order.order_status.id === 2 && (
                <button
                  className="bg-primary text-white px-6 py-2 rounded-lg flex items-center justify-center sm:justify-start"
                  onClick={handleMoveToPreparing}
                >
                  Move to Preparation
                  <span className="ml-2">→</span>
                </button>
              )}

              {order.order_status.id === 3 && (
                <button
                  className="bg-primary text-white px-6 py-2 rounded-lg flex items-center justify-center sm:justify-start"
                  onClick={handleMoveToReady}
                >
                  Mark as Ready
                  <span className="ml-2">→</span>
                </button>
              )}

              {order.order_status.id === 4 && (
                <button
                  className="bg-primary text-white px-6 py-2 rounded-lg flex items-center justify-center sm:justify-start"
                  onClick={handleServeOrder}
                >
                  Serve Order
                  <span className="ml-2">→</span>
                </button>
              )}

              {order.order_status.id === 5 && (
                <div className="relative">
                  <button
                    className={`${
                      !isPaymentConfirmed
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-primary"
                    } text-white px-6 py-2 rounded-lg flex items-center justify-center sm:justify-start`}
                    onClick={() => {
                      if (isPaymentConfirmed) {
                        handleCompleteOrder();
                      } else {
                        message.warning(
                          "Please confirm payment before completing the order"
                        );
                      }
                    }}
                    disabled={!isPaymentConfirmed}
                  >
                    Complete Order
                    {!isPaymentConfirmed && (
                      <Icon icon="mdi:lock" className="ml-2 text-lg" />
                    )}
                    {isPaymentConfirmed && <span className="ml-2">→</span>}
                  </button>

                  {!isPaymentConfirmed && (
                    <div className="absolute top-full left-0 mt-2 bg-yellow-50 p-2 rounded text-xs text-yellow-700 border border-yellow-200">
                      <div className="flex items-center">
                        <Icon icon="mdi:information" className="mr-1" />
                        Confirm payment to unlock this action
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="space-y-4">
          <CustomerInfo customer={order.user} />

          {/* Pass the localPaymentStatus to PaymentDetails */}
          <PaymentDetails
            payment={
              payment
                ? {
                    ...payment,
                    order: {
                      ...payment.order,
                      payment_status_id:
                        localPaymentStatus !== undefined
                          ? localPaymentStatus
                          : payment.order.payment_status_id,
                    },
                  }
                : null
            }
          />

          {/* Payment Button Section - Only show if user has payment permission */}
          {canProcessPayments && (
            <div className="space-y-2">
              {/* Initial Payment Button - Only shown when no payment has been made */}
              {!isPaymentPending && !isPaymentConfirmed && (
                <button
                  onClick={handleConfirmPayment}
                  className="bg-primary hover:bg-primaryHover text-white w-full py-2 rounded-lg flex items-center justify-center"
                >
                  Confirm Payment
                  <Icon icon="mdi:check-bold" className="ml-2" />
                </button>
              )}

              {/* Pending Payment Status - Shown when payment_status_id = 1 */}
              {isPaymentPending && !isPaymentConfirmed && (
                <div className="space-y-2">
                  <div className="bg-yellow-100 border border-yellow-300 rounded-lg p-3 text-yellow-700 text-sm">
                    <div className="flex items-center mb-1">
                      <Icon icon="mdi:clock-check" className="mr-2" />
                      <span className="font-semibold">Payment Pending</span>
                    </div>
                    <p>Payment has been initiated but not yet confirmed.</p>
                  </div>

                  <button
                    onClick={() => setIsPaymentModalOpen(true)}
                    className="bg-green-600 hover:bg-green-700 text-white w-full py-2 rounded-lg flex items-center justify-center"
                  >
                    Complete Payment
                    <Icon icon="mdi:cash-check" className="ml-2" />
                  </button>
                </div>
              )}

              {/* Confirmed Payment Status - Shown when payment_status_id = 2 */}
              {isPaymentConfirmed && (
                <div className="bg-green-100 border border-green-300 rounded-lg p-3 text-green-700">
                  <div className="flex items-center">
                    <Icon icon="mdi:check-circle" className="mr-2 text-lg" />
                    <span className="font-semibold">Payment Confirmed</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Payment Confirmation Modal (in a separate component) */}
      <PaymentConfirmationModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        onConfirm={handleCompletePayment}
        orderTotal={calculateTotal()}
        orderReference={order.reference}
      />

      {/* Order Completion Modal */}
      <OrderCompletionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleCompleteOrder}
      />
    </div>
  );
};

export default OrderDetailsPage;
